import { useEffect, useRef, useState } from "react";

interface IUseNewArrivalIdsArgs<TItem, TId extends string | number> {
    open: boolean;
    items: TItem[];
    getId: (item: TItem) => TId;
    durationMs?: number;
}

const DEFAULT_NEW_ARRIVAL_DURATION_MS = 3000;
const EMPTY_NEW_ARRIVAL_IDS = new Set<string | number>();

const collectArrivedIds = <TItem, TId extends string | number>(
    items: TItem[],
    getId: (item: TItem) => TId,
    knownIds: Set<TId>
): TId[] => {
    const arrivedIds: TId[] = [];
    items.forEach((item) => {
        const id = getId(item);
        if (!knownIds.has(id)) {
            knownIds.add(id);
            arrivedIds.push(id);
        }
    });
    return arrivedIds;
};

const addIdsToSet = <TId>(set: Set<TId>, ids: TId[]): Set<TId> => {
    const next = new Set(set);
    ids.forEach((id) => next.add(id));
    return next;
};

export const useNewArrivalIds = <TItem, TId extends string | number>({
    open,
    items,
    getId,
    durationMs = DEFAULT_NEW_ARRIVAL_DURATION_MS,
}: IUseNewArrivalIdsArgs<TItem, TId>) => {
    const [newArrivalIds, setNewArrivalIds] = useState<Set<TId>>(() => new Set());
    const knownIdsRef = useRef<Set<TId>>(new Set());
    const timeoutIdsRef = useRef<Map<TId, number>>(new Map());
    const generationRef = useRef(0);
    const wasOpenRef = useRef(false);

    const clearTimeouts = () => {
        timeoutIdsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
        timeoutIdsRef.current.clear();
    };

    const removeArrivedId = (id: TId, generation: number) => {
        if (generation !== generationRef.current) return;

        setNewArrivalIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        timeoutIdsRef.current.delete(id);
    };

    const scheduleRemoval = (id: TId, generation: number) => {
        const existingTimeoutId = timeoutIdsRef.current.get(id);
        if (existingTimeoutId) window.clearTimeout(existingTimeoutId);

        const timeoutId = window.setTimeout(() => removeArrivedId(id, generation), durationMs);
        timeoutIdsRef.current.set(id, timeoutId);
    };

    const markIdsAsArrived = (arrivedIds: TId[], generation: number) => {
        if (generation !== generationRef.current) return;
        setNewArrivalIds((prev) => addIdsToSet(prev, arrivedIds));
    };

    useEffect(() => {
        if (!open) {
            generationRef.current += 1;
            wasOpenRef.current = false;
            knownIdsRef.current = new Set();
            clearTimeouts();
            return;
        }

        if (!wasOpenRef.current) {
            generationRef.current += 1;
            knownIdsRef.current = new Set(items.map(getId));
            clearTimeouts();
            wasOpenRef.current = true;
            return;
        }

        const arrivedIds = collectArrivedIds(items, getId, knownIdsRef.current);
        if (arrivedIds.length === 0) return;

        const generation = generationRef.current;

        arrivedIds.forEach((id) => scheduleRemoval(id, generation));
        window.setTimeout(() => markIdsAsArrived(arrivedIds, generation));
    }, [durationMs, getId, items, open]);

    useEffect(() => () => clearTimeouts(), []);

    return (open ? newArrivalIds : EMPTY_NEW_ARRIVAL_IDS) as Set<TId>;
};
